import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, Flame, MessageSquare } from "lucide-react";

const STATUS_STYLE = {
  responding: "border-[#FF3B30]/40 bg-[#FF3B30]/15 text-[#FF5247]",
  on_scene: "border-[#FFC107]/40 bg-[#FFC107]/10 text-[#FFC107]",
  contained: "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]",
};

export default function IncidentFeed({ incidents, updatedAt, onSelect, homeIds, areaIds, watchedIds }) {
  const [nowTs, setNowTs] = useState(Date.now());

  useEffect(() => {
    const i = setInterval(() => setNowTs(Date.now()), 10000);
    return () => clearInterval(i);
  }, []);

  const ago = (iso) => {
    const s = Math.max(0, (nowTs - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${Math.floor(s)}S AGO`;
    if (s < 3600) return `${Math.floor(s / 60)}M AGO`;
    return `${Math.floor(s / 3600)}H AGO`;
  };

  return (
    <div
      data-testid="incident-feed"
      className="flex h-[420px] flex-col border border-[#27272A] bg-[#0D0D0D] lg:h-[560px]"
    >
      <div className="flex items-center justify-between border-b border-[#27272A] px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="signal-dot inline-block h-1.5 w-1.5 rounded-full bg-[#FF3B30]" />
          <span className="font-jb text-[11px] tracking-[0.3em] text-white">
            LIVE INCIDENTS
          </span>
        </div>
        <span
          data-testid="incident-count"
          className="font-jb text-[11px] tracking-[0.2em] text-zinc-500"
        >
          {incidents.length} ACTIVE
        </span>
      </div>
      <div className="feed-scroll flex-1 overflow-y-auto">
        <AnimatePresence initial={false}>
          {incidents.map((inc) => (
            <motion.div
              key={inc.id}
              layout
              initial={{ opacity: 0, y: -18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.4 }}
              data-testid={`incident-card-${inc.id}`}
              onClick={() => onSelect?.(inc.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && onSelect?.(inc.id)}
              className={`group w-full cursor-pointer border-b border-[#1C1C1C] p-5 text-left transition-colors duration-300 hover:bg-[#121212] focus:outline-none focus:ring-2 focus:ring-[#FF3B30] ${
                homeIds?.has(inc.id)
                  ? "border-l-2 border-l-[#FF6B00] bg-[#FF6B00]/5"
                  : areaIds?.has(inc.id)
                    ? "border-l-2 border-l-[#FF3B30] bg-[#FF3B30]/5"
                    : watchedIds?.has(inc.id)
                      ? "border-l-2 border-l-[#FFC107] bg-[#FFC107]/5"
                      : ""
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <p className="font-chivo text-sm font-bold tracking-tight text-white">
                  {inc.type}
                </p>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                  {homeIds?.has(inc.id) && (
                    <span
                      data-testid={`home-badge-${inc.id}`}
                      className="border border-[#FF6B00]/40 bg-[#FF6B00]/15 px-2 py-0.5 font-jb text-[9px] tracking-[0.2em] text-[#FF6B00]"
                    >
                      HOME
                    </span>
                  )}
                  {watchedIds?.has(inc.id) && (
                    <span
                      data-testid={`watch-badge-${inc.id}`}
                      className="border border-[#FFC107]/40 bg-[#FFC107]/10 px-2 py-0.5 font-jb text-[9px] tracking-[0.2em] text-[#FFC107]"
                    >
                      WATCHED
                    </span>
                  )}
                  {areaIds?.has(inc.id) && (
                    <span
                      data-testid={`area-badge-${inc.id}`}
                      className="border border-[#FF3B30]/40 bg-[#FF3B30]/15 px-2 py-0.5 font-jb text-[9px] tracking-[0.2em] text-[#FF5247]"
                    >
                      YOUR AREA
                    </span>
                  )}
                  <span
                    className={`border px-2 py-0.5 font-jb text-[9px] tracking-[0.2em] ${STATUS_STYLE[inc.bucket] || STATUS_STYLE.on_scene}`}
                  >
                    {inc.status.replace("_", " ")}
                  </span>
                </div>
              </div>
              <p className="mt-2 font-jb text-[11px] leading-relaxed text-zinc-400">
                {[inc.address, inc.locality].filter(Boolean).join(", ")}
              </p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {inc.appliances.map((a, i) => (
                  <span
                    key={`${inc.id}-${i}`}
                    title={a.name}
                    className="border border-[#27272A] bg-black/40 px-2 py-0.5 font-jb text-[10px] text-zinc-300"
                  >
                    {a.callsign}
                  </span>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between font-jb text-[9px] tracking-[0.2em] text-zinc-600">
                <span className="flex items-center gap-1.5">
                  <Flame className="h-3 w-3 text-[#FF6B00]" />
                  {inc.code} · {inc.agency}
                </span>
                <span className="flex items-center gap-2">
                  {inc.comments > 0 && (
                    <span
                      data-testid={`comment-count-${inc.id}`}
                      className="flex items-center gap-1 text-[#FFC107]"
                    >
                      <MessageSquare className="h-3 w-3" />
                      {inc.comments}
                    </span>
                  )}
                  {ago(inc.started_at)}
                  <ChevronRight className="h-3.5 w-3.5 text-zinc-600 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-[#FF3B30]" />
                </span>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {incidents.length === 0 && (
          <p
            data-testid="incident-feed-empty"
            className="p-6 font-jb text-[11px] tracking-[0.2em] text-zinc-600"
          >
            ACQUIRING SIGNAL…
          </p>
        )}
      </div>
      <div className="border-t border-[#27272A] px-5 py-3">
        <p className="font-jb text-[9px] leading-relaxed tracking-[0.25em] text-zinc-600">
          FOR PRIVATE USE ONLY — IN AN EMERGENCY GO TO{" "}
          <a
            href="https://www.emergency.vic.gov.au"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#FF5247] underline underline-offset-2"
          >
            VICEMERGENCY.GOV.AU
          </a>
        </p>
      </div>
    </div>
  );
}
